import './index.less';
import Icon from '@/components/iconfont/Icon.jsx';
import ControlBar from './ControlBar/index.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { localStreams } from '@/utils/media.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import CustomPlayerBar from '@/pages/recording/CustomPlayerBar/index.jsx';
import { throttle, debounce } from 'radash';
import {
  isSlideFile,
  isVideoFile,
  RECORD_READY_COUNT,
  RECORD_STATE,
  setCameraMirror,
} from '@/store/slices/recording.js';
import SlidePlayer from '@/pages/recording/SlidePlayer/index.jsx';
import CountDown from '@/pages/recording/CountDown/index.jsx';
import EmptyLogo from '@/assets/recording/empty-logo.png';
import { getCompletedRecordInfo, getPreviewMediaUrl } from '@/utils/recordMedia.js';
import VolumeIndicator from '@/components/volumeIndicator/VolumeIndicator.jsx';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '@/hooks/DrawerHooks.js';
import { projectController, userService } from '@/api/index.js';
import { setUserDetailState } from '@/store/slices/userDetail.js';
import { getToken } from '@/utils/cookie.js';
import { setProjectCount } from '@/store/slices/home.js';
import { useTranslation } from 'react-i18next';

export default function Recording() {
  const navigate = useNavigate();
  function returnHome() {
    navigate('/home');
  }
  const { setIsOpen } = useDrawer();
  const { t } = useTranslation();

  const cameraOn = useSelector((state) => state.recording.cameraOn);
  const microphoneOn = useSelector((state) => state.recording.microphoneOn);
  const screenOn = useSelector((state) => state.recording.screenOn);
  const fileOn = useSelector((state) => state.recording.fileOn);
  const fileType = useSelector((state) => state.recording.fileType);
  const fileVideoUrl = useSelector((state) => state.recording.fileVideoUrl);
  const fileSlideUrls = useSelector((state) => state.recording.fileSlideUrls);
  const state = useSelector((state) => state.recording.state);
  const volume = useSelector((state) => state.recording.volume);
  const isFreeUser =
    useSelector((state) => state.userDetail.userDetailState.userSubscription?.level) === 1;

  useEffect(() => {
    if (getToken()) {
      userService.userDetail().then((res) => {
        if (res?.code === 0) {
          dispatch(setUserDetailState({ key: 'userDetailState', value: res.data }));
        }
      });
      projectController.list().then((res) => {
        if (res.code === 0) {
          dispatch(setProjectCount(res.data?.length || 0));
        }
      });
    }
  }, []);

  const cameraPlayer = useRef(null);
  useEffect(() => {
    if (cameraPlayer.current) {
      if (cameraOn) {
        cameraPlayer.current.srcObject = localStreams.getCameraStream();
        cameraPlayer.current.onloadedmetadata = function () {
          calculateLayout();
        };
      } else {
        calculateLayout();
        cameraPlayer.current.load();
        cameraPlayer.current.srcObject = localStreams.getCameraStream();
      }
    }
  }, [cameraOn]);

  const screenPlayer = useRef(null);
  useEffect(() => {
    if (screenPlayer.current) {
      if (screenOn) {
        screenPlayer.current.srcObject = localStreams.getScreenStream();
        screenPlayer.current.onloadedmetadata = function () {
          debounceCalcLayout();
        };
      } else {
        calculateLayout();
        screenPlayer.current.load();
        screenPlayer.current.srcObject = localStreams.getScreenStream();
      }
    }
  }, [screenOn]);
  useEffect(() => {
    if (screenOn) {
      screenPlayer.current.onresize = debounceCalcLayout;
    } else {
      screenPlayer.current.onresize = null;
    }
  }, [screenOn, cameraOn, fileOn]);

  const [fileVideoDuration, setFileVideoDuration] = useState(0);
  const [fileVideoTime, setFileVideoTime] = useState(0);
  const fileVideoPlayer = useRef(null);
  useEffect(() => {
    if (fileVideoPlayer.current) {
      setFileVideoTime(0);
      if (fileOn) {
        if (isVideoFile(fileType)) {
          fileVideoPlayer.current.src = fileVideoUrl;
          fileVideoPlayer.current.onloadedmetadata = function () {
            calculateLayout();
            setFileVideoDuration(fileVideoPlayer.current.duration);
          };
          fileVideoPlayer.current.ontimeupdate = function () {
            setFileVideoTime(this.currentTime);
          };
        }
      } else {
        calculateLayout();
        fileVideoPlayer.current.load();
        fileVideoPlayer.current.src = '';
      }
    }
  }, [fileOn, fileType, fileVideoUrl, fileSlideUrls]);

  const playFileVideo = useCallback(() => {
    fileVideoPlayer.current.play();
  }, []);

  const pauseFileVideo = useCallback(() => {
    fileVideoPlayer.current.pause();
  }, []);

  const changeFileVideoTime = useCallback((time) => {
    fileVideoPlayer.current.currentTime = time;
    setFileVideoTime(time);
  }, []);

  function calcOneVideoLayout(itemWidth, itemHeight, wrapperWidth, wrapperHeight) {
    const style = {
      minWidth: '100%',
      height: '100%',
    };
    if (wrapperWidth / wrapperHeight > itemWidth / itemHeight) {
      style.minWidth = `${(itemWidth * wrapperHeight) / itemHeight}px`;
    } else {
      style.height = `${(itemHeight * wrapperWidth) / itemWidth}px`;
    }
    return style;
  }

  function calcTwoVideoLayout(
    leftItemWidth,
    leftItemHeight,
    rightItemWidth,
    rightItemHeight,
    wrapperWidth,
    wrapperHeight,
  ) {
    const expectedLeftWidth = wrapperWidth - 260;
    const style = {
      left: {},
      right: {},
    };

    if (expectedLeftWidth / wrapperHeight > leftItemWidth / leftItemHeight) {
      const realLeftWidth = (leftItemWidth * wrapperHeight) / leftItemHeight;
      style.left = {
        marginRight: '20px',
        minWidth: `${realLeftWidth}px`,
        height: '100%',
      };
      const realRightWidth = wrapperWidth - realLeftWidth - 20;
      const rightStyle = calcOneVideoLayout(
        rightItemWidth,
        rightItemHeight,
        realRightWidth,
        wrapperHeight,
      );
      if (rightStyle.minWidth === '100%') {
        rightStyle.minWidth = realRightWidth;
      }
      style.right = rightStyle;
    } else {
      const realRightWidth = 240;
      style.right = {
        minWidth: `${realRightWidth}px`,
        height: `${(realRightWidth * rightItemHeight) / rightItemWidth}px`,
      };
      const realLeftWidth = wrapperWidth - 260;
      style.left = {
        marginRight: '20px',
        minWidth: `${realLeftWidth}px`,
        height: `${(realLeftWidth * leftItemHeight) / leftItemWidth}px`,
      };
    }
    return style;
  }

  const [cameraStyle, setCameraStyle] = useState({});
  const [screenStyle, setScreenStyle] = useState({});
  const [fileStyle, setFileStyle] = useState({});
  function calculateLayout() {
    const wrapper = document.querySelector('.recording-page > main');
    if (!wrapper) {
      return;
    }

    const wrapperWidth = wrapper.clientWidth - 40;
    const wrapperHeight = wrapper.clientHeight - 40;

    if (!screenOn && !fileOn && cameraOn) {
      const { videoWidth, videoHeight } = cameraPlayer.current;
      setCameraStyle(calcOneVideoLayout(videoWidth, videoHeight, wrapperWidth, wrapperHeight));
    }

    if (!cameraOn) {
      setCameraStyle({
        minWidth: `0`,
        height: '0',
      });
    }

    if (screenOn && !cameraOn) {
      const { videoWidth, videoHeight } = screenPlayer.current;
      setScreenStyle(calcOneVideoLayout(videoWidth, videoHeight, wrapperWidth, wrapperHeight));
    }

    if (!screenOn) {
      setScreenStyle({
        minWidth: `0`,
        height: '0',
      });
    }

    if (fileOn && !cameraOn) {
      let fileWidth, fileHeight;
      if (isVideoFile(fileType)) {
        fileWidth = fileVideoPlayer.current.videoWidth;
        fileHeight = fileVideoPlayer.current.videoHeight;
      } else {
        const canvas = document.querySelector('.recording-page .slide-player canvas');
        fileWidth = canvas.width;
        fileHeight = canvas.height;
      }
      setFileStyle(calcOneVideoLayout(fileWidth, fileHeight, wrapperWidth, wrapperHeight));
    }

    if (!fileOn) {
      setFileStyle({
        minWidth: `0`,
        height: '0',
      });
    }

    if (screenOn && cameraOn) {
      const { videoWidth: screenVideoWidth, videoHeight: screenVideoHeight } = screenPlayer.current;
      const { videoWidth: cameraVideoWidth, videoHeight: cameraVideoHeight } = cameraPlayer.current;

      const style = calcTwoVideoLayout(
        screenVideoWidth,
        screenVideoHeight,
        cameraVideoWidth,
        cameraVideoHeight,
        wrapperWidth,
        wrapperHeight,
      );
      setScreenStyle(style.left);
      setCameraStyle(style.right);
    }

    if (fileOn && cameraOn) {
      const { videoWidth: cameraVideoWidth, videoHeight: cameraVideoHeight } = cameraPlayer.current;
      let fileWidth, fileHeight;
      if (isVideoFile(fileType)) {
        fileWidth = fileVideoPlayer.current.videoWidth;
        fileHeight = fileVideoPlayer.current.videoHeight;
      } else {
        const canvas = document.querySelector('.recording-page .slide-player canvas');
        fileWidth = canvas.width;
        fileHeight = canvas.height;
      }

      const style = calcTwoVideoLayout(
        fileWidth,
        fileHeight,
        cameraVideoWidth,
        cameraVideoHeight,
        wrapperWidth,
        wrapperHeight,
      );
      setFileStyle(style.left);
      setCameraStyle(style.right);
    }
  }

  const debounceCalcLayout = debounce({ delay: 100 }, calculateLayout);
  const throttle1CalcLayout = throttle({ interval: 50 }, calculateLayout);
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      throttle1CalcLayout();
    });
    observer.observe(document.querySelector('.recording-page > main'));
    return () => {
      observer?.disconnect();
    };
  }, [cameraOn, screenOn, fileOn]);

  let title = '';
  if (
    [RECORD_STATE.NOT_STARTED, RECORD_STATE.READY].includes(state) &&
    (cameraOn || microphoneOn || screenOn || fileOn)
  ) {
    title = t('As_Recording_Header1');
  }
  if ([RECORD_STATE.IN_PROGRESS, RECORD_STATE.PAUSED].includes(state)) {
    title = (
      <span className="is-recording">
        <i />
        {t('As_Recording_Header2')}
      </span>
    );
  }
  if (state === RECORD_STATE.COMPLETED) {
    title = 'Video processing will be completed shortly';
  }
  if (state === RECORD_STATE.CREATED) {
    title = getCompletedRecordInfo().name;
  }
  if (isFreeUser) {
    title = t('As_Recording_FreeLimit1');
  }

  const [screenLoading, setScreenLoading] = useState(false);
  const screenRecordFunction = useRef(null);
  function setScreenRecord(func) {
    screenRecordFunction.current = func;
  }
  function applyScreenRecord() {
    screenRecordFunction.current && screenRecordFunction.current();
  }

  const cameraMirror = useSelector((state) => state.recording.cameraMirror);
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(setCameraMirror(false));
    };
  }, []);
  function toggleCameraMirror() {
    dispatch(setCameraMirror(!cameraMirror));
  }

  const previousState = useRef();
  const [onlyPreviewAudio, setOnlyPreviewAudio] = useState(false);
  const [previewLeftStyle, setPreviewLeftStyle] = useState({});
  const previewLeftPlayer = useRef(null);
  const [previewRightStyle, setPreviewRightStyle] = useState({});
  const previewRightPlayer = useRef(null);
  const previewAudioPlayer = useRef(new Audio());
  const [previewDuration, setPreviewDuration] = useState(0);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewPaused, setPreviewPaused] = useState(true);
  const previewObserver = useRef(null);
  useEffect(() => {
    // 开启预览
    if (previousState.current !== RECORD_STATE.COMPLETED && state === RECORD_STATE.COMPLETED) {
      const { leftURL, rightURL, audioURL, leftMirror, rightMirror } = getPreviewMediaUrl();
      setOnlyPreviewAudio(!leftURL && !rightURL && audioURL);

      function calcPreviewLayout() {
        const twoLayout = leftURL && rightURL;
        const oneLayout = (leftURL && !rightURL) || (!leftURL && rightURL);
        const wrapper = document.querySelector('.recording-page > main');
        if (wrapper) {
          const wrapperWidth = wrapper.clientWidth - 40;
          const wrapperHeight = wrapper.clientHeight - 40;
          if (twoLayout) {
            const style = calcTwoVideoLayout(
              previewLeftPlayer.current.videoWidth,
              previewLeftPlayer.current.videoHeight,
              previewRightPlayer.current.videoWidth,
              previewRightPlayer.current.videoHeight,
              wrapperWidth,
              wrapperHeight,
            );
            if (leftMirror) {
              style.left.transform = 'rotateY(180deg)';
            }
            if (rightMirror) {
              style.right.transform = 'rotateY(180deg)';
            }
            setPreviewLeftStyle(style.left);
            setPreviewRightStyle(style.right);
          }
          if (oneLayout) {
            const style = calcOneVideoLayout(
              previewLeftPlayer.current.videoWidth,
              previewLeftPlayer.current.videoHeight,
              wrapperWidth,
              wrapperHeight,
            );
            if (leftMirror) {
              style.transform = 'rotateY(180deg)';
            }
            setPreviewLeftStyle(style);
          }
        }
      }

      async function loadMedia() {
        const loadArray = [];
        setPreviewTime(0);
        if (leftURL) {
          previewLeftPlayer.current.src = leftURL;
          loadArray.push(
            new Promise((resolve) => {
              previewLeftPlayer.current.onloadedmetadata = resolve;
            }),
          );
        }
        if (rightURL) {
          previewRightPlayer.current.src = rightURL;
          loadArray.push(
            new Promise((resolve) => {
              previewRightPlayer.current.onloadedmetadata = resolve;
            }),
          );
        }
        if (audioURL) {
          previewAudioPlayer.current.src = audioURL;
          loadArray.push(
            new Promise((resolve) => {
              previewAudioPlayer.current.onloadedmetadata = resolve;
            }),
          );
        }
        await Promise.all(loadArray);
        const media = leftURL ? previewLeftPlayer.current : previewAudioPlayer.current;
        media.onended = function () {
          setPreviewPaused(true);
          setPreviewTime(
            this.duration === Infinity ? getCompletedRecordInfo().time : this.duration,
          );
        };
        media.ontimeupdate = function () {
          setPreviewTime(this.currentTime);
        };
        media.ondurationchange = function () {
          setPreviewDuration(
            this.duration === Infinity ? getCompletedRecordInfo().time : this.duration,
          );
        };
        setPreviewDuration(
          media.duration === Infinity ? getCompletedRecordInfo().time : media.duration,
        );
        setPreviewPaused(true);

        calcPreviewLayout();
      }
      loadMedia();

      const throttleCalcPreviewLayout = throttle({ interval: 50 }, calcPreviewLayout);
      previewObserver.current = new ResizeObserver(() => {
        throttleCalcPreviewLayout();
      });
      previewObserver.current.observe(document.querySelector('.recording-page > main'));
    }
    previousState.current = state;
    if (![RECORD_STATE.COMPLETED, RECORD_STATE.CREATED].includes(state)) {
      stopPreview();
      if (previewObserver.current) {
        previewObserver.current.disconnect();
        previewObserver.current = null;
      }
    }
  }, [state]);

  const playPreview = useCallback(() => {
    const media = onlyPreviewAudio ? previewAudioPlayer.current : previewLeftPlayer.current;
    if (
      media.currentTime / previewDuration > 0.99 ||
      media.currentTime / getCompletedRecordInfo().time > 0.99
    ) {
      changePreviewTime(0);
    }
    if (previewLeftPlayer.current.seekable.length > 0) {
      previewLeftPlayer.current.play();
    }
    if (previewRightPlayer.current.seekable.length > 0) {
      previewRightPlayer.current.play();
    }
    if (previewAudioPlayer.current.seekable.length > 0) {
      previewAudioPlayer.current.play();
    }
    setPreviewPaused(false);
  }, [onlyPreviewAudio, previewDuration]);

  const pausePreview = useCallback(() => {
    if (previewLeftPlayer.current.seekable.length > 0) {
      previewLeftPlayer.current.pause();
    }
    if (previewRightPlayer.current.seekable.length > 0) {
      previewRightPlayer.current.pause();
    }
    if (previewAudioPlayer.current.seekable.length > 0) {
      previewAudioPlayer.current.pause();
    }
    setPreviewPaused(true);
  }, []);

  const changePreviewTime = useCallback((time) => {
    if (previewLeftPlayer.current.seekable.length > 0) {
      previewLeftPlayer.current.currentTime = time;
    }
    if (previewRightPlayer.current.seekable.length > 0) {
      previewRightPlayer.current.currentTime = time;
    }
    if (previewAudioPlayer.current.seekable.length > 0) {
      previewAudioPlayer.current.currentTime = time;
    }
    setPreviewPaused(true);
  }, []);

  function stopPreview() {
    setScreenLoading(false);
    setPreviewLeftStyle({});
    setPreviewRightStyle({});
    if (previewLeftPlayer.current) {
      previewLeftPlayer.current.load();
      previewLeftPlayer.current.src = undefined;
    }
    if (previewRightPlayer.current) {
      previewRightPlayer.current.load();
      previewRightPlayer.current.src = undefined;
    }
    if (previewAudioPlayer.current) {
      previewAudioPlayer.current.load();
      previewAudioPlayer.current.src = undefined;
    }
  }
  useEffect(() => {
    return stopPreview;
  }, []);

  return (
    <div className="recording-page">
      <header>
        <div className="return" onClick={returnHome}>
          <Icon name="icon-arrow-right"></Icon>
          {t('As_Home_NavTitle1')}
        </div>

        <span className="title">{title}</span>

        {isFreeUser && (
          <button className="btn btn-link" onClick={setIsOpen}>
            {t('As_Recording_Upgrade')}
          </button>
        )}
      </header>

      <main>
        {!cameraOn &&
        !microphoneOn &&
        !screenOn &&
        !fileOn &&
        ![RECORD_STATE.COMPLETED, RECORD_STATE.CREATED].includes(state) ? (
          <section className="empty">
            <img src={EmptyLogo} alt="" />
            <span>{t('As_Recording_Nothing1')}</span>
            <button className="btn btn-primary" onClick={applyScreenRecord}>
              {screenLoading ? (
                <span className="loading loading-spinner"></span>
              ) : (
                t('As_Recording_Nothing2')
              )}
            </button>
          </section>
        ) : null}

        {!cameraOn &&
        microphoneOn &&
        !screenOn &&
        !fileOn &&
        ![RECORD_STATE.COMPLETED, RECORD_STATE.CREATED].includes(state) ? (
          <section className="only-microphone">
            <VolumeIndicator volume={volume}></VolumeIndicator>
          </section>
        ) : null}

        <section style={fileStyle}>
          <div className={`file-wrapper ${isVideoFile(fileType) ? 'show' : ''}`}>
            <video ref={fileVideoPlayer} muted crossOrigin="anonymous"></video>

            <div style={{ position: 'absolute', bottom: '20px', width: '100%', padding: '0 20px' }}>
              <CustomPlayerBar
                duration={fileVideoDuration}
                time={fileVideoTime}
                handlePlay={playFileVideo}
                handlePause={pauseFileVideo}
                handleChangeTime={changeFileVideoTime}></CustomPlayerBar>
            </div>
          </div>

          <div className={`file-wrapper ${isSlideFile(fileType) ? 'show' : ''}`}>
            <SlidePlayer urls={fileSlideUrls} waitAll={true} onLoad={calculateLayout}></SlidePlayer>
          </div>
        </section>

        <section style={screenStyle}>
          <video ref={screenPlayer} muted autoPlay></video>
        </section>

        <section style={cameraStyle}>
          <video
            ref={cameraPlayer}
            className={`${cameraMirror && 'mirror'}`}
            muted
            autoPlay></video>
          <button
            className={`mirror-icon ${cameraMirror && 'mirror'}`}
            onClick={toggleCameraMirror}
            disabled={state !== RECORD_STATE.NOT_STARTED}>
            <Icon name="icon-mirror"></Icon>
          </button>
        </section>

        {state === RECORD_STATE.READY ? <CountDown count={RECORD_READY_COUNT}></CountDown> : null}

        <div
          className={`preview ${[RECORD_STATE.COMPLETED, RECORD_STATE.CREATED].includes(state) ? 'show' : ''}`}>
          <section style={previewLeftStyle}>
            <video ref={previewLeftPlayer} muted></video>
          </section>

          <section style={previewRightStyle}>
            <video ref={previewRightPlayer} muted></video>
          </section>

          {onlyPreviewAudio ? <div className="only-audio">🎙</div> : null}

          {previewPaused ? (
            <div className="play-btn" onClick={playPreview}>
              <Icon name="icon-play"></Icon>
            </div>
          ) : null}

          <div style={{ position: 'absolute', bottom: '20px', width: '100%', padding: '0 20px' }}>
            <CustomPlayerBar
              duration={previewDuration}
              time={previewTime}
              isPaused={previewPaused}
              handlePlay={playPreview}
              handlePause={pausePreview}
              handleChangeTime={changePreviewTime}></CustomPlayerBar>
          </div>
        </div>
      </main>

      <footer>
        <ControlBar
          screenRecordCb={setScreenRecord}
          setOuterScreenLoading={setScreenLoading}></ControlBar>
      </footer>
    </div>
  );
}
